import { FRONTEND_ERRORLOG_URL } from "../constants";

// Structured payload accepted by logFrontendError. The shape is intentionally
// strict so the backend can index/aggregate on known fields. Callers should
// only put safe values here — the serializer drops custom Error fields, but
// anything placed directly on this wrapper (or in `details`) is logged as-is.
//
// GDPR: do NOT place user input, response bodies, or directly identifying
// data (Salesforce lead IDs, emails, business IDs, etc.) on this wrapper.
export type FrontendErrorContext = {
    info: string;
    error?: unknown;
    status?: number;
    orderId?: string;
    url?: string;
    componentStack?: string;
    details?: Record<string, unknown>;
};

// Error.message / .name / .stack are non-enumerable, so JSON.stringify(new Error())
// returns "{}". Walk the value and convert Errors into plain objects.
//
// GDPR: only the standard name/message/stack fields are emitted for Errors —
// custom fields (e.g. an HTTP response body attached for diagnostics) are
// dropped, since they may contain user data or other sensitive content. Walking
// of plain wrapper objects is preserved so callers can attach explicit safe
// context (info strings, status codes), but it is the caller's responsibility
// not to put PII into that wrapper.
function serializeForLog(value: unknown): unknown {
    if (value === null || value === undefined) return value;
    if (typeof value !== "object") return value;

    if (value instanceof Error) {
        return {
            name: value.name,
            message: value.message,
            stack: value.stack,
        };
    }

    if (Array.isArray(value)) return value.map(serializeForLog);

    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
        result[key] = serializeForLog(val);
    }
    return result;
}

export async function logFrontendError(context: FrontendErrorContext) {
    let errorString = "";

    try {
        errorString = JSON.stringify(serializeForLog(context));
    } catch {
        try {
            errorString = String(context);
        } catch {
            errorString = "Unknown error";
        }
    }

    try {
        await fetch(FRONTEND_ERRORLOG_URL, {
            method: "POST",
            body: JSON.stringify({ error: errorString }),
            headers: {
                "Content-Type": "application/json",
            }
        });
    } catch {
    }
}